import React, { useRef, useEffect, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

// Set your Mapbox access token (you'll get this free from mapbox.com)
mapboxgl.accessToken =
  "pk.eyJ1IjoieW91ci11c2VybmFtZSIsImEiOiJjbHh4eHh4eHh4eHh4In0.your-token-here";

const MapboxMap = ({ location = "both", height = "400px" }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [lng] = useState(34.8444);
  const [lat] = useState(32.1672);
  const [zoom] = useState(8);

  const locations = {
    herzliya: {
      coordinates: [34.84444527431377, 32.16721951284615],
      title: "מרפאת הרצליה",
      address: "רחוב סוקולוב 27, הרצליה",
      zoom: 16,
    },
    beersheba: {
      coordinates: [34.81328667346476, 31.24529224862607],
      title: "מרפאת באר שבע",
      address: "אליהו נאוי 24 (מתחם וורקיז קומת כניסה), באר שבע",
      zoom: 16,
    },
    both: {
      center: [34.84254438780725, 32.168041356830706],
      zoom: 8,
    },
  };

  // Custom medical practice styling
  const medicalMapStyle = {
    version: 8,
    sources: {
      "mapbox-streets": {
        type: "vector",
        url: "mapbox://mapbox.mapbox-streets-v8",
      },
    },
    layers: [
      // Base background
      {
        id: "background",
        type: "background",
        paint: {
          "background-color": "#f8fafb",
        },
      },
      // Water areas with medical blue
      {
        id: "water",
        type: "fill",
        source: "mapbox-streets",
        "source-layer": "water",
        paint: {
          "fill-color": "#e3f2fd",
        },
      },
      // Land areas
      {
        id: "land",
        type: "fill",
        source: "mapbox-streets",
        "source-layer": "landuse",
        paint: {
          "fill-color": "#f5f7fa",
        },
      },
      // Buildings with subtle medical theme
      {
        id: "buildings",
        type: "fill",
        source: "mapbox-streets",
        "source-layer": "building",
        paint: {
          "fill-color": "#e8f4f8",
          "fill-outline-color": "#01727a",
        },
      },
      // Roads
      {
        id: "roads",
        type: "line",
        source: "mapbox-streets",
        "source-layer": "road",
        paint: {
          "line-color": "#ffffff",
          "line-width": 2,
        },
      },
      // Major roads
      {
        id: "major-roads",
        type: "line",
        source: "mapbox-streets",
        "source-layer": "road",
        filter: ["in", "class", "primary", "secondary", "trunk"],
        paint: {
          "line-color": "#ffffff",
          "line-width": 3,
        },
      },
    ],
    glyphs: "mapbox://fonts/mapbox/{fontstack}/{range}.pbf",
  };

  useEffect(() => {
    if (map.current) return; // Initialize map only once

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/light-v11", // Clean light style
      center:
        location === "both"
          ? locations.both.center
          : locations[location].coordinates,
      zoom:
        location === "both" ? locations.both.zoom : locations[location].zoom,
      language: "he",
      localIdeographFontFamily:
        "'Noto Sans Hebrew', 'Arial Unicode MS', Arial, sans-serif",
    });

    // Add navigation controls
    map.current.addControl(new mapboxgl.NavigationControl(), "top-left");
    map.current.addControl(new mapboxgl.FullscreenControl(), "top-left");

    // Custom marker HTML with medical styling
    const createMarkerElement = (locationData) => {
      const el = document.createElement("div");
      el.innerHTML = `
        <div style="
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #01727a 0%, #0288a3 100%);
          border: 4px solid white;
          border-radius: 50%;
          box-shadow: 0 4px 15px rgba(1, 114, 122, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transform: scale(1);
          transition: all 0.3s ease;
        ">
          <div style="
            width: 20px;
            height: 20px;
            background: white;
            border-radius: 50%;
          "></div>
        </div>
      `;

      // Add hover effects
      el.addEventListener("mouseenter", () => {
        el.style.transform = "scale(1.1)";
      });
      el.addEventListener("mouseleave", () => {
        el.style.transform = "scale(1)";
      });

      return el;
    };

    // Create popup content with modern medical styling
    const createPopupContent = (locationData) => `
      <div style="
        direction: rtl;
        text-align: center;
        padding: 20px;
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        min-width: 250px;
      ">
        <h3 style="
          margin: 0 0 10px 0;
          color: #01727a;
          font-size: 18px;
          font-weight: 600;
        ">${locationData.title}</h3>
        <p style="
          margin: 0 0 15px 0;
          font-size: 14px;
          color: #666;
          line-height: 1.4;
        ">${locationData.address}</p>
        <div style="
          display: flex;
          gap: 10px;
          justify-content: center;
          margin-top: 15px;
        ">
          <a href="https://waze.com/ul?q=${encodeURIComponent(
            locationData.address
          )}&navigate=yes" 
             target="_blank" 
             style="
               background: linear-gradient(135deg, #01727a 0%, #0288a3 100%);
               color: white;
               padding: 8px 16px;
               text-decoration: none;
               border-radius: 25px;
               font-size: 12px;
               font-weight: 500;
               transition: all 0.3s ease;
               box-shadow: 0 2px 8px rgba(1, 114, 122, 0.3);
             ">
            🗺️ ווייז
          </a>
          <a href="https://maps.google.com/?q=${encodeURIComponent(
            locationData.address
          )}" 
             target="_blank"
             style="
               background: linear-gradient(135deg, #4285f4 0%, #34a853 100%);
               color: white;
               padding: 8px 16px;
               text-decoration: none;
               border-radius: 25px;
               font-size: 12px;
               font-weight: 500;
               transition: all 0.3s ease;
               box-shadow: 0 2px 8px rgba(66, 133, 244, 0.3);
             ">
            📍 גוגל מפות
          </a>
        </div>
      </div>
    `;

    // Add markers based on location prop
    if (location === "both") {
      // Add both markers
      Object.keys(locations).forEach((key) => {
        if (key !== "both") {
          const locationData = locations[key];
          const marker = new mapboxgl.Marker(createMarkerElement(locationData))
            .setLngLat(locationData.coordinates)
            .addTo(map.current);

          const popup = new mapboxgl.Popup({
            offset: 25,
            closeButton: true,
            closeOnClick: false,
          }).setHTML(createPopupContent(locationData));

          marker.getElement().addEventListener("click", () => {
            popup.addTo(map.current).setLngLat(locationData.coordinates);
          });
        }
      });
    } else {
      // Add single marker
      const locationData = locations[location];
      const marker = new mapboxgl.Marker(createMarkerElement(locationData))
        .setLngLat(locationData.coordinates)
        .addTo(map.current);

      const popup = new mapboxgl.Popup({
        offset: 25,
        closeButton: true,
        closeOnClick: false,
      }).setHTML(createPopupContent(locationData));

      marker.getElement().addEventListener("click", () => {
        popup.addTo(map.current).setLngLat(locationData.coordinates);
        map.current.flyTo({
          center: locationData.coordinates,
          zoom: 18,
          duration: 2000,
        });
      });
    }

    // Map styling after load
    map.current.on("load", () => {
      // Add subtle medical-themed styling
      map.current.setPaintProperty("water", "fill-color", "#e3f2fd");

      // Customize building colors for medical feel
      if (map.current.getLayer("building")) {
        map.current.setPaintProperty("building", "fill-color", "#f0f8ff");
        map.current.setPaintProperty("building", "fill-opacity", 0.8);
      }
    });
  }, [location]);

  // Cleanup function
  useEffect(() => {
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  return (
    <div
      ref={mapContainer}
      style={{
        width: "100%",
        height: height,
        borderRadius: "20px",
        overflow: "hidden",
        boxShadow: "0 8px 32px rgba(1, 114, 122, 0.15)",
        border: "1px solid rgba(1, 114, 122, 0.1)",
      }}
    />
  );
};

export default MapboxMap;
